import { useToast } from '@src/common/hooks/useToast';
import type { Order, OrderPermitApplication } from '@src/orders/domain/Order';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import { useSubmitPermit } from '@src/permits/hooks/useSubmitPermit';
import { ProcessWithJurisdiction } from '@src/permits/processWithJurisdiction/ProcessWithJurisdiction';
import type { ProcessWithJurisdictionDocument } from '@src/permits/SubmitPermitForm';
import { useTranslation } from 'react-i18next';

interface Props {
  orderPermitApplication: OrderPermitApplication;
  order: Order;
}

export const ProcessWithJurisdictionPermit = ({
  orderPermitApplication,
  order,
}: Props) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const { refreshOrders } = useRefreshOrders();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();

  const redirectToOrderDetail = () => {
    void navigateToOrderDetails({ orderId: order.id });
  };

  const { submitPermit } = useSubmitPermit({
    permitApplicationId: orderPermitApplication.application.id,
    onSuccess: () => {
      showSuccessToast(t('submitConfirmationNumber.success'));
      refreshOrders();
      redirectToOrderDetail();
    },
  });

  const onSubmit = async (data: {
    confirmationNumber: string;
    documents?: ProcessWithJurisdictionDocument[];
  }) => {
    try {
      await submitPermit(data);
    } catch {
      showErrorToast(t('submitConfirmationNumber.error'));
    }
  };

  return (
    <ProcessWithJurisdiction
      orderPermitApplication={orderPermitApplication}
      order={order}
      onSubmit={onSubmit}
    />
  );
};
